import { Input as InputPrimitive } from '@base-ui/react/input';
import * as React from 'react';

import { controlChromeVariants } from '@playbooks/ui/components/component-recipes';
import { controlHeightClasses } from '@playbooks/ui/components/control-size';
import { cn } from '@playbooks/ui/lib/utils';

import { cva, VariantProps } from 'class-variance-authority';

const inputVariants = cva(
	cn(
		controlChromeVariants(),
		'w-full min-w-0 py-1 font-normal file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
	),
	{
		variants: {
			size: {
				xs: `${controlHeightClasses.xs} px-2 text-xs`,
				sm: `${controlHeightClasses.sm} px-2.5 text-sm`,
				default: `${controlHeightClasses.default} px-3 text-base md:text-sm`,
				lg: `${controlHeightClasses.lg} px-3 text-base`,
			},
			variant: {
				default: '',
				group:
					'h-full flex-1 rounded-none border-0 bg-transparent shadow-none hover:border-transparent focus-visible:border-transparent focus-visible:ring-0 group-has-[[data-align=inline-start]]/input-group:pl-2 group-has-[[data-align=inline-end]]/input-group:pr-2',
			},
		},
		defaultVariants: {
			size: 'default',
			variant: 'default',
		},
	},
);

const inputClassName = inputVariants();
type InputProps = Omit<React.ComponentProps<'input'>, 'size'> & VariantProps<typeof inputVariants>;

function Input({ className, type, size, variant, ...props }: InputProps) {
	// Theme
	const rootClassName = cn(inputVariants({ size, variant }), className);

	// Render
	return (
		<InputPrimitive
			type={type}
			data-slot='input'
			data-size={size || 'default'}
			data-variant={variant || 'default'}
			className={rootClassName}
			{...props}
		/>
	);
}

export { Input, inputClassName, inputVariants };
export type { InputProps };
